'use client';
import * as React from 'react';
import type { RefObject } from '@mui/x-internals/types';
import { useGridRegisterPipeProcessor } from '@mui/x-data-grid-pro/internals';
import type { GridPipeProcessor } from '@mui/x-data-grid-pro/internals';
import type { GridPrivateApiPremium } from '../../../models/gridApiPremium';
import type { DataGridPremiumProcessedProps } from '../../../models/dataGridPremiumProps';
import { wrapColumnWithFormula, unwrapColumnFromFormula } from './wrapColumnWithFormula';
import { GRID_FORMULA_ROW_NUMBER_FIELD } from './gridFormulaPositionContext';
import { GRID_FORMULA_ROW_NUMBER_COL_DEF } from './gridFormulaRowNumberColDef';

export const useGridFormulaPreProcessors = (
  apiRef: RefObject<GridPrivateApiPremium>,
  props: Pick<
    DataGridPremiumProcessedProps,
    'disableFormulas' | 'dataSource' | 'formulaA1Notation'
  >,
) => {
  const updateFormulaColumns = React.useCallback<GridPipeProcessor<'hydrateColumns'>>(
    (columnsState) => {
      const formulasEnabled = !props.disableFormulas && !props.dataSource;

      const a1NotationEnabled = formulasEnabled && !!props.formulaA1Notation;
      columnsState.orderedFields.forEach((field) => {
        let column = unwrapColumnFromFormula(columnsState.lookup[field]);
        if (formulasEnabled && column.allowFormulas) {
          column = wrapColumnWithFormula(column, apiRef, a1NotationEnabled);
        }
        columnsState.lookup[field] = column;
      });

      // Inject/remove the autogenerated row-number column for A1 notation. Its
      // leftmost position comes from the pinned-left model (set in
      // `useDataGridPremiumProps`); the column-pinning pre-processor, registered
      // after this one, moves it to the front of the left section.
      const shouldHaveRowNumberColumn = formulasEnabled && props.formulaA1Notation;
      const hasRowNumberColumn = columnsState.lookup[GRID_FORMULA_ROW_NUMBER_FIELD] != null;
      if (shouldHaveRowNumberColumn && !hasRowNumberColumn) {
        columnsState.lookup[GRID_FORMULA_ROW_NUMBER_FIELD] = GRID_FORMULA_ROW_NUMBER_COL_DEF;
        columnsState.orderedFields = [GRID_FORMULA_ROW_NUMBER_FIELD, ...columnsState.orderedFields];
      } else if (!shouldHaveRowNumberColumn && hasRowNumberColumn) {
        delete columnsState.lookup[GRID_FORMULA_ROW_NUMBER_FIELD];
        columnsState.orderedFields = columnsState.orderedFields.filter(
          (field) => field !== GRID_FORMULA_ROW_NUMBER_FIELD,
        );
      }

      return columnsState;
    },
    [apiRef, props.disableFormulas, props.dataSource, props.formulaA1Notation],
  );

  useGridRegisterPipeProcessor(apiRef, 'hydrateColumns', updateFormulaColumns);
};
